<template>
  <b-modal
    id="campaign-add-new"
    :title="`${isEdit ? 'Edit' : 'Create'} Campaign`"
    :no-close-on-backdrop="true"
    cancel-variant="outline-secondary"
    scrollable
    size="lg"
    @show="initData"
    @hide="() => {
      resetData()
      $emit('hide')
    }"
  >
    <validation-observer ref="rules">
      <b-form
        v-if="!isLoading"
        ref="form"
        @submit.stop.prevent="handleCreate"
      >
        <b-row>
          <b-col
            cols="12"
          >
            <b-form-group
              :label="$t('contract.labels.campaignName')"
              label-for="campaignName"
            >
              <validation-provider
                #default="{ errors }"
                name="Campaign Name"
                rules="required"
              >
                <b-form-input
                  id="campaignName"
                  v-model.trim="campaignName"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('contract.placeholders.campaignName')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
          >
            <b-form-group
              :label="$t('contract.labels.campaignType')"
              label-for="campaignType"
            >
              <b-form-checkbox
                id="campaignType"
                v-model="isAreaCampaign"
                switch
                @input="onToggleCampaignType"
              >
                {{ $t('area') }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="isAreaCampaign">
          <b-col
            cols="12"
          >
            <b-form-group
              :label="$t('contract.labels.select_areas')"
              label-for="areas"
            >
              <v-select
                id="areas"
                v-model="areas"
                :options="areaOptions"
                multiple
                :reduce="val => val.value"
                :placeholder="$t('contract.placeholders.areas')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Only when create new campaign -->
        <b-row v-if="!isEdit">
          <b-col
            cols="12"
          >
            <b-form-group>
              <b-form-checkbox
                id="isApplyPastShifts"
                v-model="isApplyPastShifts"
                switch
              >
                {{ $t('contract.labels.applyPastShifts') }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <b-row
              ref="row"
            >
              <b-col md="6">
                <b-form-group
                  :label="$t('contract.labels.startDate')"
                  label-for="start"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Start date"
                    rules="required"
                  >
                    <flat-pickr
                      v-model="fromStr"
                      class="form-control"
                      :config="{ time_24hr: true }"
                      @on-change="(date) => { setDate(date, 'start') }"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                  :label="$t('contract.labels.endDate')"
                  label-for="end"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="End date"
                    rules="required"
                  >
                    <flat-pickr
                      v-model="toStr"
                      class="form-control"
                      :config="{ minDate: startDate, time_24hr: true }"
                      @on-change="(date) => { setDate(date, 'end') }"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col v-if="checkInvalidCampaign" cols="12">
                <small class="text-danger">{{ $t('contract.warningText.existCampaign') }}</small>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <b-form-group
              :label="$t('contract.labels.announcement')"
              label-for="announcement"
            >
              <v-select
                id="announcement"
                v-model="announcement"
                :options="announcementOptions"
                :reduce="val => val.value"
                :placeholder="$t('contract.placeholders.announcement')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col cols="12">
            <app-collapse style="width: 100%; margin: 0 auto; padding: 0">
              <app-collapse-item
                :title="$t('contract.labels.price')"
                style="text-transform: capitalize"
                is-visible
              >
                <validation-provider
                  #default="{ errors }"
                  name="Price"
                  rules="required"
                >
                  <div
                    v-for="(item, index) in priceItems"
                    :key="`price-item-${index}`"
                    class="d-flex align-items-end"
                  >
                    <div class="w-100">
                      <b-row>
                        <b-col cols="9">
                          <b-form-group
                            :label="$t('name')"
                          >
                            <v-select
                              v-model.trim="item.name"
                              :options="typeReqOptions"
                              :reduce="val => val"
                              :placeholder="$t('contract.placeholders.priceName')"
                              @option:deselected="item.name = ''"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <b-form-group
                            :label="$t('contract.labels.price')"
                          >
                            <b-form-input
                              v-model.trim="item.value"
                              :placeholder="$t('contract.placeholders.priceValue')"
                              type="number"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                    <div>
                      <b-button
                        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                        size="sm"
                        variant="outline-danger"
                        class="mb-1 ml-2"
                        style="height: 37px;"
                        @click="handleRemoveRowItem(index, 'price')"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="18"
                        />
                      </b-button>
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <b-row>
                  <b-col cols="12">
                    <b-button
                      variant="outline-primary"
                      block
                      @click="handleAddRowItem('price')"
                    >
                      {{ $t('contract.btn.addPrice') }}
                    </b-button>
                  </b-col>
                </b-row>
              </app-collapse-item>
            </app-collapse>
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col cols="12">
            <app-collapse style="width: 100%; margin: 0 auto; padding: 0">
              <app-collapse-item
                :title="$t('contract.labels.salary_short')"
                style="text-transform: capitalize"
                is-visible
              >
                <validation-provider
                  #default="{ errors }"
                  name="Salary"
                  rules="required"
                >
                  <div
                    v-for="(item, index) in salaryItems"
                    :key="`salary-item-${index}`"
                    class="d-flex align-items-end"
                  >
                    <div class="w-100">
                      <b-row>
                        <b-col cols="9">
                          <b-form-group
                            :label="$t('name')"
                          >
                            <v-select
                              v-model.trim="item.name"
                              :options="typeReqOptions"
                              :reduce="val => val"
                              :placeholder="$t('contract.placeholders.salaryName')"
                              @option:deselected="item.name = ''"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <b-form-group
                            :label="$t('contract.labels.salary_short')"
                          >
                            <b-form-input
                              v-model.trim="item.value"
                              :placeholder="$t('contract.placeholders.salaryValue')"
                              type="number"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                    <div>
                      <b-button
                        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                        size="sm"
                        variant="outline-danger"
                        class="mb-1 ml-2"
                        style="height: 37px;"
                        @click="handleRemoveRowItem(index, 'salary')"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="18"
                        />
                      </b-button>
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <b-row>
                  <b-col cols="12">
                    <b-button
                      variant="outline-primary"
                      block
                      @click="handleAddRowItem('salary')"
                    >
                      {{ $t('contract.btn.addSalary') }}
                    </b-button>
                  </b-col>
                </b-row>
              </app-collapse-item>
            </app-collapse>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <div
      v-if="isLoading"
      class="m-1 d-flex align-items-center justify-content-center"
    >
      <b-spinner large />
      <span style="margin-left:5px; font-size: 17px">{{ $t('loading') }}</span>
    </div>

    <template #modal-footer="{ cancel }">
      <b-button
        variant="outline-secondary"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </b-button>
      <b-button
        variant="primary"
        :disabled="!campaignName || !fromStr || !toStr || checkInvalidPrice || checkInvalidSalary || isSubmitting || checkInvalidCampaign"
        @click="handleCreate"
      >
        {{ isEdit ? $t('save') : $t('create') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BButton, BModal, BRow, BCol, BForm, BFormGroup, BFormInput, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import flatPickr from 'vue-flatpickr-component'
import AppCollapse from '@hw-ui/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@hw-ui/components/app-collapse/AppCollapseItem.vue'
import vSelect from 'vue-select'
import { capitalize } from '@hw-core/utils'
import { fetchCampaignList } from '@/shared/services/contractService'
import { getAreas } from '@/shared/services/areaService'

export default {
  components: {
    AppCollapse,
    AppCollapseItem,
    BButton,
    BModal,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    flatPickr,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    docItem: {
      type: Object,
      default: null,
    },
    checkCampaignExist: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      required,
      campaignName: '',
      startDate: null,
      endDate: null,
      fromStr: '',
      toStr: '',
      announcement: null,
      announcementOptions: [],
      priceItems: [],
      salaryItems: [],
      isAreaCampaign: false,
      areas: [],
      areaOptions: [],
      processer: {},
      updates: {
        content: [],
        date: '',
        processer: '',
        state: '',
      },
      isLoading: false,
      isSubmitting: false,
      isApplyPastShifts: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.docItem
    },
    selectedAccount() {
      return this.$store.state.appAccount.selectedAccount
    },
    typeReqOptions() {
      const roles = this.$store.state.appAccount.roles.map(role => role.label)
      return ['default', ...roles, 'Love']
    },
    checkInvalidPrice() {
      let inValid = false
      if (!this.priceItems || !this.priceItems.length) {
        inValid = true
      } else {
        const match = this.priceItems.findIndex(item => !item.name || item.name.length < 1 || !item.value || item.value < 1)
        if (match !== -1) {
          inValid = true
        }
      }
      return inValid
    },
    checkInvalidSalary() {
      let inValid = false
      if (!this.salaryItems || !this.salaryItems.length) {
        inValid = true
      } else {
        const match = this.salaryItems.findIndex(item => item.name.length < 1 || !item.value || item.value < 1)
        if (match !== -1) {
          inValid = true
        }
      }
      return inValid
    },
    checkInvalidCampaign() {
      return this.checkCampaignExist({
        id: this.isEdit ? this.docItem.id : null,
        startDate: this.startDate,
        endDate: this.endDate, 
      })
    }
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          firstName: user.firstName,
          lastName: user.lastName,
        }
      })
  },
  methods: {
    onConvertDateToString(date) {
      const convertedDate = new Date(date)
      return (`${
        convertedDate.getFullYear().toString().padStart(4, '0')}-${
        (convertedDate.getMonth() + 1).toString().padStart(2, '0')}-${
        convertedDate.getDate().toString().padStart(2, '0')} ${
        convertedDate.getHours().toString().padStart(2, '0')}:${
        convertedDate.getMinutes().toString().padStart(2, '0')}`
      )
    },
    setDate(date, slot) {
      if (slot === 'start') {
        [this.startDate] = date
      }
      if (slot === 'end') {
        [this.endDate] = date
        if (this.endDate) {
          this.endDate.setHours(23, 59, 59, 0)
        }
      }
    },
    handleCreate() {
      this.$refs.rules.validate().then(async success => {
        if (success) {
          this.isSubmitting = true
          const formattedAreas = this.areas.map(item => (item instanceof Object ? item.value : item))
          if (this.isEdit) {
            this.updates = {
              content: [],
              date: new Date(),
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              state: 'updated',
            }
            if (this.campaignName !== this.docItem.name) {
              this.updates.content.push(`Name: ${this.campaignName}`)
            }
            if (this.startDate.getTime() !== this.docItem.startDate.toDate().getTime()) {
              this.updates.content.push(`Start date: ${this.startDate.toLocaleDateString('fi')}`)
            }
            if (this.endDate.getTime() !== this.docItem.endDate.toDate().getTime()) {
              this.updates.content.push(`End date: ${this.endDate.toLocaleDateString('fi')}`)
            }
            if (!(this.announcement === this.docItem.announcement)) {
              if (!this.announcement) {
                this.updates.content.push('Remove announcement')
              } else {
                let newAnnouncement = ''
                const match = this.announcementOptions.findIndex(opt => opt.value === this.announcement)
                if (match !== -1) {
                  newAnnouncement = this.announcementOptions[match].label
                }
                this.updates.content.push(`Announcement changed: ${newAnnouncement}`)
              }
            }

            if (!(formattedAreas.length === this.docItem.areas.length
            && this.docItem.areas.every(value => formattedAreas.includes(value.id)))) {
              if (formattedAreas.length === 0) {
                this.updates.content.push('Remove areas')
              } else {
                const newAreas = []
                formattedAreas.forEach(value => {
                  const match = this.areaOptions.findIndex(opt => opt.value === value)
                  if (match !== -1) {
                    newAreas.push(this.areaOptions[match].label)
                  }
                })
                this.updates.content.push(`Areas changed: ${newAreas.join(', ')}`)
              }
            }
            this.salaryItems.forEach(salaryItem => {
              if (!this.docItem.salary[salaryItem.name] || (this.docItem.salary[salaryItem.name] && salaryItem.value !== this.docItem.salary[salaryItem.name])) {
                this.updates.content.push(`Salary ${salaryItem.name}: ${salaryItem.value}`)
              }
            })

            this.priceItems.forEach(priceItem => {
              if (!this.docItem.price[priceItem.name] || (this.docItem.price[priceItem.name] && priceItem.value !== this.docItem.price[priceItem.name])) {
                this.updates.content.push(`Price ${priceItem.name}: ${priceItem.value}`)
              }
            })

            Object.keys(this.docItem.price).forEach(key => {
              if (this.priceItems.findIndex(priceItem => priceItem.name === key) === -1) {
                this.updates.content.push(`Removed: Price ${key}`)
              }
            })

            Object.keys(this.docItem.salary).forEach(key => {
              if (this.salaryItems.findIndex(salaryItem => salaryItem.name === key) === -1) {
                this.updates.content.push(`Removed: Salary ${key}`)
              }
            })
          } else {
            this.updates = {
              content: ['Created campaign'],
              date: new Date(),
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              state: 'created',
            }
          }
          const oldUpdates = this.docItem && this.docItem.updates ? this.docItem.updates : []
          const newUpdates = this.updates.content.length ? [this.updates] : []
          const updates = this.isEdit ? newUpdates.concat(oldUpdates) : newUpdates
          const salary = {}
          const price = {}

          this.salaryItems.forEach(salaryItem => {
            salary[salaryItem.name.toLowerCase()] = +salaryItem.value
          })
          this.priceItems.forEach(priceItem => {
            price[priceItem.name.toLowerCase()] = +priceItem.value
          })

          const data = {
            id: this.isEdit ? this.docItem.id : null,
            accountId: this.selectedAccount.value.id,
            announcement: this.announcement,
            areas: formattedAreas,
            created: this.isEdit ? this.docItem.created : new Date(),
            creator: `${this.processer.firstName} ${this.processer.lastName}`,
            startDate: this.startDate,
            endDate: this.endDate,
            name: this.campaignName,
            deleted: this.isEdit ? this.docItem.deleted : false,
            price,
            salary,
            isApplyPastShifts: this.isEdit ? false : this.isApplyPastShifts,
            type: 'campaign',
            updates,
          }

          this.$emit('create-or-update-campaign', {
            data,
            callback: () => {
              this.$bvModal.hide('campaign-add-new')
              this.isSubmitting = false
            },
          })
        }
      })
    },
    async initData() {
      this.isLoading = true
      const announcements = await fetchCampaignList('campaign')
      this.announcementOptions = announcements.map(item => ({
            value: item.id,
            label: capitalize(item.title),
      }))
      const areas = await getAreas(this.selectedAccount.value.id)
      this.areaOptions = areas.map(item => ({
          value: item.id,
          label: capitalize(item.name),
        }))

      if (this.isEdit) {
        this.campaignName = this.docItem.name
        this.startDate = this.docItem.startDate.toDate()
        this.endDate = this.docItem.endDate.toDate()
        this.fromStr = this.onConvertDateToString(this.docItem.startDate.toDate())
        this.toStr = this.onConvertDateToString(this.docItem.endDate.toDate())
        this.announcement = this.docItem.announcement
        this.areas = this.docItem.areas && this.docItem.areas.length ? this.docItem.areas.map(area => ({
          value: area.id,
          label: capitalize(area.name),
        })) : []
        this.isAreaCampaign = !!(this.docItem.areas && this.docItem.areas.length)

        const priceItems = Object.keys(this.docItem.price).map(priceKey => ({
          name: priceKey,
          value: this.docItem.price[priceKey],
        }))

        const salaryItems = []
        for (let i = 0; i < Object.keys(this.docItem.salary).length; i += 1) {
          const salaryKey = Object.keys(this.docItem.salary)[i]
          salaryItems.push({
            name: salaryKey,
            value: this.docItem.salary[salaryKey],
          })
        }

        this.priceItems = priceItems
        this.salaryItems = salaryItems
        this.isLoading = false
      } else {
        this.resetData()
      }
    },
    handleRemoveRowItem(index, type = 'price') {
      if (index === 0) {
        return
      }
      if (type === 'price') {
        this.priceItems.splice(index, 1)
      } else {
        this.salaryItems.splice(index, 1)
      }
    },
    handleAddRowItem(type = 'price') {
      if (type === 'price') {
        this.priceItems.push({
          name: '',
          value: null,
        })
      } else {
        this.salaryItems.push({
          name: '',
          value: null,
        })
      }
    },
    resetData() {
      this.campaignName = ''
      this.startDate = null
      this.endDate = null
      this.fromStr = ''
      this.toStr = ''
      this.announcement = null
      this.priceItems = []
      this.salaryItems = []
      this.areas = []
      this.isAreaCampaign = false
      this.isLoading = false
    },
    onToggleCampaignType() {
      if (this.isAreaCampaign === false) {
        this.areas = []
      }
    },
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
  ::v-deep .modal-lg {
    max-width: 550px;
  }
</style>
